import {useState, useEffect, useContext} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GlobalContext from '../context/GlobalContext.js';
import getTaskFlowDetails from '../functions/API/getTaskFlowDetails.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import NewFieldForm from '../forms/newField.js';
import ConditionForm from '../forms/conditionForm.js';
import AutoOutcomeForm from '../forms/autoOutcomeForm.js';
import Loader from '../components/loader.js'
import DeleteModal from '../modals/delete.js'
import FlowEditor from '../components/flowEditor/FlowEditor.js'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogTitle, DialogDescription, DialogBody } from '../components/dialog.jsx'
import { ArrowLeft } from 'lucide-react'

export default function TaskFlowDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { settings } = useContext(GlobalContext);
    const paymentsEnabled = settings?.payments_enabled === 1 && !!settings?.paymentprovider;
    const [taskFlow, setTaskFlow] = useState(null);
    const [steps, setSteps] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stepToDelete, setStepToDelete] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);

    // Field management state
    const [newFieldForm, setNewFieldForm] = useState(false);
    const [fieldEditMode, setFieldEditMode] = useState(false);
    const [selectedField, setSelectedField] = useState(null);
    const [currentStepId, setCurrentStepId] = useState(null);
    const [fieldToDelete, setFieldToDelete] = useState(null);
    const [fieldDeleteModalOpen, setFieldDeleteModalOpen] = useState(false);

    // Condition form state
    const [conditionFormOpen, setConditionFormOpen] = useState(false);
    const [conditionTargetStep, setConditionTargetStep] = useState(null);

    // Auto-outcome form state
    const [autoOutcomeFormOpen, setAutoOutcomeFormOpen] = useState(false);
    const [autoOutcomeSourceStep, setAutoOutcomeSourceStep] = useState(null);

    // Edit mode state for conditions and auto-outcomes
    const [conditionEditMode, setConditionEditMode] = useState(false);
    const [autoOutcomeEditMode, setAutoOutcomeEditMode] = useState(false);
    const [autoOutcomeRuleIndex, setAutoOutcomeRuleIndex] = useState(null);


    async function GetAPIData() {
        try {
            const TaskFlowResponse = await getTaskFlowDetails(id);

            if (TaskFlowResponse.response.ok && TaskFlowResponse.data && TaskFlowResponse.data.task_flow) {
                const flowData = TaskFlowResponse.data.task_flow;
                setTaskFlow(flowData);

                if (flowData.steps && Array.isArray(flowData.steps)) {
                    setSteps(flowData.steps);
                } else {
                    setSteps([]);
                }
            } else {
                toast.error('Failed to load task flow details.');
                navigate('/taskflows');
                return;
            }

            setIsLoaded(true);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('An error occurred while loading data.');
            setIsLoaded(true);
        }
    }

    function handleDeleteClick(step){
        setStepToDelete(step);
        setDeleteModalOpen(true);
    }

    function refreshData(){
        setShouldRefresh(shouldRefresh + 1);
    }

    // Field handlers
    function handleAddField(step){
        setFieldEditMode(false);
        setSelectedField(null);
        setCurrentStepId(step.id);
        setNewFieldForm(true);
    }

    function handleEditField(field){
        setFieldEditMode(true);
        setSelectedField(field);
        setCurrentStepId(field.task_flow_step_id);
        setNewFieldForm(true);
    }

    function handleDeleteField(field){
        setFieldToDelete(field);
        setFieldDeleteModalOpen(true);
    }

    function handleAddCondition(targetStep){
        setConditionEditMode(false);
        setConditionTargetStep(targetStep);
        setConditionFormOpen(true);
    }

    function handleAddAutoOutcome(sourceStep){
        setAutoOutcomeEditMode(false);
        setAutoOutcomeRuleIndex(null);
        setAutoOutcomeSourceStep(sourceStep);
        setAutoOutcomeFormOpen(true);
    }

    function handleEditCondition(targetStep){
        setConditionEditMode(true);
        setConditionTargetStep(targetStep);
        setConditionFormOpen(true);
    }

    function handleEditAutoOutcome(sourceStep, ruleIndex){
        setAutoOutcomeEditMode(true);
        setAutoOutcomeRuleIndex(ruleIndex);
        setAutoOutcomeSourceStep(sourceStep);
        setAutoOutcomeFormOpen(true);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh, id]);


    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div className="space-y-3">
                        {/* Header */}
                        {taskFlow && (
                            <div className="flex items-center gap-3">
                                <Button variant="ghost" size="icon" onClick={() => navigate('/taskflows')}>
                                    <ArrowLeft className="h-4 w-4" />
                                </Button>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h1 className="text-lg font-semibold text-gray-900 truncate">{taskFlow.name}</h1>
                                        {taskFlow.is_archived && <Badge variant="destructive">Archived</Badge>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                        {taskFlow.tenant_requestable && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Tenant Requestable</Badge>}
                                        {taskFlow.is_onboarding && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Onboarding</Badge>}
                                        {taskFlow.admin_assignable && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Admin Assignable</Badge>}
                                        {taskFlow.has_deadline && (
                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{taskFlow.deadline_days} {taskFlow.deadline_type} day deadline</Badge>
                                        )}
                                        <span className="text-xs text-gray-400">
                                            {steps.length} {steps.length === 1 ? 'step' : 'steps'}
                                        </span>
                                        {taskFlow.help_url && (
                                            <a href={taskFlow.help_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                                Docs
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Visual Flow Editor */}
                        <FlowEditor
                            steps={steps}
                            taskFlow={taskFlow}
                            taskFlowId={id}
                            paymentsEnabled={paymentsEnabled}
                            sharepointEnabled={true}
                            onRefresh={refreshData}
                            onDeleteStep={handleDeleteClick}
                            onAddField={handleAddField}
                            onEditField={handleEditField}
                            onDeleteField={handleDeleteField}
                            onAddCondition={handleAddCondition}
                            onAddAutoOutcome={handleAddAutoOutcome}
                            onEditCondition={handleEditCondition}
                            onEditAutoOutcome={handleEditAutoOutcome}
                        />
                    </div>
                )}
                <DeleteModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    IDToDelete={stepToDelete?.id}
                    deleteAction="deleteStep"
                    refresh={refreshData}
                    title="Delete Step"
                    message="Are you sure you want to delete this step? This action cannot be undone."
                />
                <Dialog open={newFieldForm} onClose={setNewFieldForm} size="2xl">
                    <DialogTitle>{fieldEditMode ? 'Edit Field' : 'Add New Field'}</DialogTitle>
                    <DialogDescription>
                        {fieldEditMode ? 'Update the field configuration' : 'Add a new field to collect information from users'}
                    </DialogDescription>
                    <DialogBody>
                        <NewFieldForm
                            key={fieldEditMode ? `edit-${selectedField?.id}` : `add-${currentStepId}`}
                            setVisible={setNewFieldForm}
                            refresh={setShouldRefresh}
                            refreshValue={shouldRefresh}
                            stepId={currentStepId}
                            editMode={fieldEditMode}
                            existingData={selectedField}
                        />
                    </DialogBody>
                </Dialog>
                <DeleteModal
                    open={fieldDeleteModalOpen}
                    setOpen={setFieldDeleteModalOpen}
                    IDToDelete={fieldToDelete?.id}
                    deleteAction="deleteField"
                    refresh={refreshData}
                    title="Delete Field"
                    message="Are you sure you want to delete this field? This action cannot be undone."
                />
                <Dialog open={conditionFormOpen} onClose={setConditionFormOpen} size="2xl">
                    <DialogTitle>{conditionEditMode ? 'Edit Visibility Conditions' : 'Add Visibility Condition'}</DialogTitle>
                    <DialogDescription>
                        {conditionEditMode
                            ? 'Edit or remove the conditions that control when this step is shown'
                            : 'Control when this step is shown based on answers from previous steps'}
                    </DialogDescription>
                    <DialogBody>
                        {conditionTargetStep && (
                            <ConditionForm
                                key={`condition-${conditionTargetStep.id}-${conditionEditMode}`}
                                setVisible={setConditionFormOpen}
                                refresh={setShouldRefresh}
                                refreshValue={shouldRefresh}
                                taskFlowId={id}
                                targetStep={conditionTargetStep}
                                editMode={conditionEditMode}
                            />
                        )}
                    </DialogBody>
                </Dialog>
                <Dialog open={autoOutcomeFormOpen} onClose={setAutoOutcomeFormOpen} size="2xl">
                    <DialogTitle>{autoOutcomeEditMode ? 'Edit Auto-Outcome Rule' : 'Add Auto-Outcome Rule'}</DialogTitle>
                    <DialogDescription>
                        {autoOutcomeEditMode
                            ? 'Edit or delete this auto-outcome rule'
                            : 'Automatically complete or close the request based on step answers'}
                    </DialogDescription>
                    <DialogBody>
                        {autoOutcomeSourceStep && (
                            <AutoOutcomeForm
                                key={`auto-outcome-${autoOutcomeSourceStep.id}-${autoOutcomeEditMode}-${autoOutcomeRuleIndex}`}
                                setVisible={setAutoOutcomeFormOpen}
                                refresh={setShouldRefresh}
                                refreshValue={shouldRefresh}
                                taskFlowId={id}
                                sourceStep={autoOutcomeSourceStep}
                                editMode={autoOutcomeEditMode}
                                ruleIndex={autoOutcomeRuleIndex}
                            />
                        )}
                    </DialogBody>
                </Dialog>
            </AdminSidebar>
        </>
    );
}
